// src/components/Pentests/tabs/tools/ProfileCombobox.js
//
// Custom dropdown for picking a vuln-scan profile. Visual mirror of
// ModuleCombobox so Recon / Port Scan / Vuln Scan launchers feel cohesive.
// Each row shows: lucide icon, label (bold), description (muted), severity
// chips + safety pill on the right.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bug,
  Check,
  ChevronDown,
  Flame,
  Globe,
  Network,
  Search,
  Settings2,
  Shield,
  Zap,
} from 'lucide-react';

const ICON_MAP = {
  zap: Zap,
  globe: Globe,
  network: Network,
  flame: Flame,
  settings: Settings2,
  bug: Bug,
};

const SEVERITY_CHIP = {
  info: 'bg-slate-100 text-slate-700 border-slate-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
};

const SAFETY_PILL = {
  safe: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  standard: 'bg-sky-100 text-sky-700 border-sky-300',
  aggressive: 'bg-amber-100 text-amber-700 border-amber-300',
  insane: 'bg-rose-100 text-rose-700 border-rose-300 shadow-[0_0_10px_rgba(244,63,94,0.35)]',
  custom: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
};

function ProfileIcon({ iconKey, className }) {
  const I = ICON_MAP[iconKey] || Shield;
  return <I className={className || 'w-4 h-4'} />;
}

function SeverityChips({ severityString }) {
  if (!severityString) return null;
  const sevs = severityString.split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <div className="flex items-center gap-1">
      {sevs.map((s) => (
        <span
          key={s}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${SEVERITY_CHIP[s] || ''}`}
        >
          {s.charAt(0)}
        </span>
      ))}
    </div>
  );
}

function SafetyPill({ level }) {
  if (!level) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.12em] ${SAFETY_PILL[level] || SAFETY_PILL.standard}`}
    >
      {level}
    </span>
  );
}

export default function ProfileCombobox({ profiles, value, onChange, placeholder = 'Select a profile' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => profiles.find((p) => (p.name || p.id) === value) || null,
    [profiles, value],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => {
      const label = (p.label || p.name || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      return label.includes(q) || desc.includes(q);
    });
  }, [profiles, filter]);

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setFilter('');
    }
  }, [open]);

  const select = (p) => {
    onChange(p.name || p.id);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full text-left rounded-lg border bg-cyber-100 px-3 py-2.5 transition outline-none ${
          open
            ? 'border-shinobi-green/70 ring-1 ring-shinobi-green/30'
            : 'border-cyber-300/60 hover:border-cyber-300'
        }`}
      >
        <div className="flex items-center gap-3">
          {selected ? (
            <ProfileIcon iconKey={selected.icon} className="w-4 h-4 text-shinobi-green shrink-0" />
          ) : (
            <Shield className="w-4 h-4 text-slate-500 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold truncate ${selected ? 'text-slate-900' : 'text-slate-500'}`}>
                {selected?.label || selected?.name || placeholder}
              </span>
              {selected && <SafetyPill level={selected.safety_level} />}
            </div>
            {selected?.description && (
              <p className="text-xs text-slate-500 truncate mt-0.5">{selected.description}</p>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-xl border border-cyber-300/60 bg-cyber-50 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-cyber-300/40 flex items-center gap-2 bg-cyber-100/40">
            <Search className="w-3.5 h-3.5 text-slate-500" />
            <input
              ref={inputRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Filter profiles..."
              className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-500 outline-none"
            />
          </div>
          <div className="max-h-96 overflow-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">No matches</div>
            ) : (
              filtered.map((p, idx) => {
                const key = p.name || p.id;
                const isSelected = (selected?.name || selected?.id) === key;
                const isHighlight = idx === highlight;
                return (
                  <button
                    key={key}
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => { e.preventDefault(); select(p); }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-l-2 transition ${
                      isSelected
                        ? 'bg-wasabi-100 border-shinobi-green'
                        : isHighlight
                          ? 'bg-cyber-200/50 border-cyber-300/50'
                          : 'border-transparent hover:bg-cyber-200/40'
                    }`}
                  >
                    <ProfileIcon
                      iconKey={p.icon}
                      className={`w-4 h-4 mt-0.5 shrink-0 ${isSelected ? 'text-shinobi-green' : 'text-slate-500'}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isSelected ? 'text-shinobi-green' : 'text-slate-900'} truncate`}>
                          {p.label || p.name}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        {p.description}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0 pt-0.5">
                      <SafetyPill level={p.safety_level} />
                      {p.name !== 'custom' && <SeverityChips severityString={p.severity} />}
                      {isSelected && <Check className="w-3.5 h-3.5 text-shinobi-green" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="px-3 py-2 border-t border-cyber-300/40 bg-cyber-100/30 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
            <span>{filtered.length} {filtered.length === 1 ? 'profile' : 'profiles'}</span>
            <span className="flex items-center gap-3">
              <span>I/L/M/H/C = severities reported</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
